import {
  Autocomplete,
  Box,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import {
  ArrowBackIcon,
  DriveFileRenameOutlineIcon,
} from "../../icons/index.tsx";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  addInspRule,
  editInspRule,
  getInspRuleDetail,
} from "../../services/inspRuleService.js";
import { getInspModels } from "../../services/modelService.ts";
import {
  handleTextFieldChange,
  regexProcessing,
  updateResult,
} from "../../utils/changeUtils.js";
import { CustomMuiButton } from "../../utils/styleUtils.js";
import { regexSeparation } from "../../utils/util.js";
import { saveInspRuleState } from "../../recoil/index.ts";
import { useRecoilState } from "recoil";
import { toast } from "react-toastify";
import { addTrain } from "../../services/trainService.ts";
import "../../index.css";

const StyledPopper = {
  "& .MuiAutocomplete-listbox": {
    maxHeight: "200px",
    overflowY: "auto",
  },
};

export default function InspRuleFormPage() {
  const navigate = useNavigate();

  const { id } = useParams();
  const inputRef = useRef(null);
  const [inspModelList, setInspModelList] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  // re
  const [initialFormData, setInitialFormData] = useState(null);
  const [savedInspRule, setSavedInspRule] = useRecoilState(saveInspRuleState);
  const [isTrainOptionClick, setIsTrainOptionClick] = useState(false);

  // 언마운트시 초기화
  useEffect(() => {
    return () => {
      setSavedInspRule({});
    };
  }, []);

  const updateFormData = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const [formData, setFormData] = useState({
    description: "",
    model: null,
    modelDescription: null,
    recipeRegex: {
      isChecked: false,
      keyword: null,
      location: null,
      result: null,
    },
    imageRegex: {
      isChecked: false,
      keyword: null,
      location: null,
      result: null,
    },
  });

  useEffect(() => {
    const initializeData = async () => {
      try {
        // 모델 목록 조회
        const modelResponse = await getInspModels({ page: 1, pageSize: 300 });
        const filteredContents = modelResponse.data.contents.map(
          ({ trainModel, ...rest }) => rest
        );
        setInspModelList(filteredContents);

        // 수정 모드인 경우 상세 데이터 조회
        if (id) {
          const detailResponse = await getInspRuleDetail(
            Number(id) || savedInspRule.id
          );
          const data = detailResponse.data;

          const recipeRegex = data.recipe_regex
            ? regexSeparation(data.recipe_regex)
            : null;
          const imageRegex = data.image_regex
            ? regexSeparation(data.image_regex)
            : null;

          const detailData = {
            description: savedInspRule.description
              ? savedInspRule.description + " cp"
              : data.description,
            model: data.model,
            modelDescription: data.model.description,
            recipeRegex: {
              isChecked: !!data.recipe_regex,
              keyword: recipeRegex ? recipeRegex[0] : null,
              location: recipeRegex ? recipeRegex[1] : null,
              result: data.recipe_regex
                ? regexProcessing(data.recipe_regex)
                : null,
            },
            imageRegex: {
              isChecked: !!data.image_regex,
              keyword: imageRegex ? imageRegex[0] : null,
              location: imageRegex ? imageRegex[1] : null,
              result: data.image_regex
                ? regexProcessing(data.image_regex)
                : null,
            },
          };

          setFormData(detailData);
          setInitialFormData(detailData);
        }
      } catch (error) {
        console.error("Data initialization failed:", error);
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    updateFormData("recipeRegex", {
      ...formData.recipeRegex,
      result: updateResult(
        formData.recipeRegex.keyword,
        formData.recipeRegex.location
      ),
    });
  }, [formData.recipeRegex.keyword, formData.recipeRegex.location]);

  useEffect(() => {
    updateFormData("imageRegex", {
      ...formData.imageRegex,
      result: updateResult(
        formData.imageRegex.keyword,
        formData.imageRegex.location
      ),
    });
  }, [formData.imageRegex.keyword, formData.imageRegex.location]);

  const handleBackClick = () => {
    navigate("/home", { state: { tab: "insp_rule" } });
  };

  const handleModelChange = (event, value) => {
    setSelectedModel(value);
    updateFormData("model", value);
    updateFormData("modelDescription", value?.description || null);
  };

  const formatRegexValue = (regex) => {
    if (!regex.isChecked) return null;
    return `${regex.keyword || ""},${regex.location || ""}`;
  };

  const getChangedData = () => {
    console.log("initialFormData", initialFormData);
    console.log("formData", formData);
    if (!initialFormData) {
      return {
        description: formData.description,
        model_uuid: formData.model?.uuid,
        recipe_regex: formatRegexValue(formData.recipeRegex),
        image_regex: formatRegexValue(formData.imageRegex),
      };
    }

    const changedData = {};
    if (formData.model?.uuid !== initialFormData.model?.uuid) {
      changedData.model_uuid = formData.model?.uuid;
    }

    const currentRecipeRegex = formatRegexValue(formData.recipeRegex);
    const initialRecipeRegex = formatRegexValue(initialFormData.recipeRegex);
    if (currentRecipeRegex !== initialRecipeRegex) {
      changedData.recipe_regex = currentRecipeRegex;
    }

    const currentImageRegex = formatRegexValue(formData.imageRegex);
    const initialImageRegex = formatRegexValue(initialFormData.imageRegex);
    if (currentImageRegex !== initialImageRegex) {
      changedData.image_regex = currentImageRegex;
    }

    if (formData.description !== initialFormData.description) {
      changedData.description = formData.description;
    }

    return changedData;
  };

  function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  const handleSubmit = async () => {
    try {
      const changedData = getChangedData();
      console.log("changedData", changedData);
      if (Object.keys(changedData).length === 0) {
        toast.info("변경된 내용이 없습니다.");
        return;
      }

      if (savedInspRule && Object.keys(savedInspRule).length > 0) {
        const response = await addInspRule({
          description: formData.description,
          recipe_regex: formatRegexValue(formData.recipeRegex),
          image_regex: formatRegexValue(formData.imageRegex),
          model_uuid: formData.model.uuid,
        });
        if (isTrainOptionClick) {
          await addTrain({
            name: response.data.name + " train",
            description: "auto-generated train " + formatDate(new Date()),
            image_period: "7",
            inspModel_id: response.data.model.id,
            material_name: formData.recipeRegex.keyword,
            trainModel_id: null,
            options: response.data.model.options || "{}",
            pre_process: response.data.model.pre_process || "[]",
            post_process: response.data.model.post_process || "[]",
          });
        }
      } else {
        const apiCall = id
          ? () => editInspRule(id, changedData)
          : () => addInspRule(changedData);
        await apiCall();
      }

      navigate("/home", { state: { tab: "insp_rule" } });
    } catch (error) {
      if (error.response.data.message) {
        toast.warning(error.response.data.message);
      }
      console.error("Submit failed:", error);
    }
  };

  const handleRegexChange = (type, field, value) => {
    updateFormData(type, {
      ...formData[type],
      [field]: value,
    });
  };

  const updateDescription = (newFullValue) => {
    setFormData((prev) => {
      const keyword = prev.recipeRegex.keyword;
      let newDescription = newFullValue;

      // keyword로 시작하면 keyword 부분 제거
      if (keyword && newFullValue.startsWith(keyword)) {
        newDescription = newFullValue.slice(keyword.length).trimStart();
      }

      return {
        ...prev,
        description: newDescription,
      };
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        p: 3,
      }}
    >
      <Box sx={{ display: "flex" }}>
        <Button
          className="add-button"
          onClick={handleBackClick}
          sx={{ color: "var(--button-color)" }}
        >
          <ArrowBackIcon /> <span>{t("InspectionRule")}</span>
        </Button>
      </Box>
      <Box sx={{ height: "100%", width: "100%", marginTop: 3 }}>
        <Typography
          sx={{
            mb: 1,
            fontWeight: "700",
            fontSize: 24,
            display: "flex",
            alignItems: "center",
          }}
          fontFamily={"NotoSansKR-Variable"}
        >
          <DriveFileRenameOutlineIcon sx={{ marginRight: "5px" }} />
          {id
            ? t("EditInspectionRule")
            : savedInspRule && Object.keys(savedInspRule).length > 0
            ? t("CopyInspectionRule")
            : t("CreateNewInspectionRule")}
        </Typography>
        <div className="input-container">
          <div className="input-container">
            <input
              type="text"
              id="input-field"
              placeholder="내용을 입력하세요"
              value={formData.description}
              onChange={(e) => updateFormData("description", e.target.value)}
              style={{
                border: "solid 1px gray",
              }}
            />
            <label htmlFor="input-field">Rule Info</label>
          </div>
          <div className="input-container">
            <Autocomplete
              disablePortal
              id="combo-box-demo"
              options={inspModelList}
              getOptionLabel={(option) => option.name}
              // PopperComponent={StyledPopper}
              sx={{ width: "100%", height: "50px" }}
              value={formData.model}
              renderOption={(props, option) => (
                <li {...props} key={option.uuid}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="모델을 선택해주세요"
                  inputRef={inputRef}
                />
              )}
              isOptionEqualToValue={(option, value) =>
                option.uuid === value.uuid
              }
              onChange={handleModelChange}
            />
            <label htmlFor="input-field">Model</label>
          </div>
          <div style={{ marginTop: "5px" }}>
            <span style={{ fontSize: "14px" }}>
              Description:
              {formData.modelDescription || "-"}
            </span>
          </div>
        </div>
        <div className="line-with-text">
          <span>Filter</span>
        </div>
        <div className="filter-container">
          <div style={{ marginBottom: "10px" }}>
            <input
              type="checkbox"
              id="horns"
              name="recipe"
              checked={formData.recipeRegex.isChecked}
              onChange={() =>
                handleRegexChange(
                  "recipeRegex",
                  "isChecked",
                  !formData.recipeRegex.isChecked
                )
              }
            />
            <span>Recipe</span>
          </div>
          <div
            className={`filter-detail ${
              formData.recipeRegex.isChecked ? "visible" : ""
            }`}
          >
            <div style={{ width: "100%", display: "flex", gap: "8px" }}>
              <div className="filter-container">
                <input
                  type="text"
                  id="option-field"
                  placeholder=".U9-"
                  value={formData.recipeRegex.keyword}
                  onChange={(e) => {
                    handleRegexChange("recipeRegex", "keyword", e.target.value);
                    updateFormData("description", e.target.value);
                  }}
                />
                <label htmlFor="filter-field">키워드</label>
              </div>
              <div className="filter-container">
                <input
                  type="text"
                  id="option-field"
                  placeholder="정수를 입력하세요(-9 ~ 9)"
                  value={formData.recipeRegex.location}
                  onChange={(e) =>
                    handleRegexChange("recipeRegex", "location", e.target.value)
                  }
                />
                <label htmlFor="filter-field">위치</label>
              </div>
            </div>
            <input
              type="text"
              id="option-field"
              placeholder=" "
              value={formData.recipeRegex.result}
            />
          </div>
        </div>
        <div className="filter-container">
          <div style={{ marginBottom: "10px" }}>
            <input
              type="checkbox"
              id="horns"
              name="filename"
              checked={formData.imageRegex.isChecked}
              onChange={() =>
                handleRegexChange(
                  "imageRegex",
                  "isChecked",
                  !formData.imageRegex.isChecked
                )
              }
            />
            <span>FileName</span>
          </div>
          <div
            className={`filter-detail ${
              formData.imageRegex.isChecked ? "visible" : ""
            }`}
          >
            <Box style={{ width: "100%", display: "flex", gap: "8px" }}>
              <div className="filter-container">
                <input
                  type="text"
                  id="option-field"
                  placeholder=".U9-"
                  value={formData.imageRegex.keyword}
                  onChange={(e) =>
                    handleRegexChange("imageRegex", "keyword", e.target.value)
                  }
                />
                <label htmlFor="filter-field">키워드</label>
              </div>
              <div className="filter-container">
                <input
                  type="text"
                  id="option-field"
                  placeholder="정수를 입력하세요(-9 ~ 9)"
                  value={formData.imageRegex.location}
                  onChange={(e) =>
                    handleRegexChange("imageRegex", "location", e.target.value)
                  }
                />
                <label htmlFor="filter-field">위치</label>
              </div>
            </Box>
            <input
              type="text"
              id="option-field"
              placeholder=" "
              value={formData.imageRegex.result}
            />
          </div>
        </div>
        <div className="line-with-text">
          <span>ETC</span>
        </div>
        <div className="filter-container">
          <div style={{ marginBottom: "10px" }}>
            <input
              type="checkbox"
              id="horns"
              name="trainOption"
              checked={isTrainOptionClick}
              onChange={() => setIsTrainOptionClick(!isTrainOptionClick)}
            />
            <span>학습옵션 추가</span>
            <Typography sx={{ mt: 1 }}>
              체크 시, 생성되는 검사규칙에 따른 학습옵션이 같이 생성됩니다.
            </Typography>
          </div>
        </div>
      </Box>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 3,
        }}
      >
        <CustomMuiButton
          onClick={handleBackClick}
          variant="text"
          label={t("Cancel")}
        />
        <CustomMuiButton
          onClick={handleSubmit}
          variant="contained"
          label={t("Apply")}
        />
      </Box>
    </Box>
  );
}
